import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CircleBranding,
  CircleIntegrations,
  circleApi,
  TribeAccountType,
} from 'src/api/circles';
import { UploadImage } from 'src/components/common/UploadImage/UploadImage';
import { VideoUpload } from 'src/components/common/VideoUpload/VideoUpload';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { fetchLoggedUser } from 'src/redux/slices/loggedUserSlice';
import Members from '../../components/common/CircleMembersCount/Members';
import { BrandingSection } from './Branding/BrandingSection';
import {
  isPlausibleEmail,
  Review360Section,
} from './Branding/Review360Section';
import { CommonField } from './CommonField/CommonField';
import styles from './EditCircle.module.scss';
import { Header } from './Header/Header';
import { DESCRIPTION_FIELD, NAME_FIELD } from './const';

export interface FormData {
  tribe_name: string;
  description: string;
  member_count_max: number;
  welcome_video_id: string;
  tribe_avatar_url: string;
  welcome_video_url: string;
  tribe_avatar_id: string;
  special_code: string;
  // null = no branding (field is only sent when it changed)
  branding: CircleBranding | null;
  // null = not connected to 360review (field is only sent when it changed)
  integrations: CircleIntegrations | null;
}

/** The 360review admin email as the form shows it. */
/** The API's message for a failed save, or a generic line. */
export const saveErrorMessage = (error: unknown): string => {
  const data = (
    error as {
      response?: { data?: { message?: unknown; errorMessage?: unknown } };
    }
  )?.response?.data;
  const message = [data?.message, data?.errorMessage].find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  );
  return message
    ? `Could not save the circle: ${message}`
    : 'Could not save the circle. Please try again.';
};

const integrationsEmail = (integrations: CircleIntegrations | null): string =>
  integrations?.review360?.account_owner_email ?? '';

/** The form text turned back into the API shape; blank means not connected. */
const integrationsFromEmail = (email: string): CircleIntegrations | null => {
  const trimmed = email.trim().toLowerCase();
  return trimmed ? { review360: { account_owner_email: trimmed } } : null;
};

export const EditCircle: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const nameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  const { id = '' } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<FormData | null>(null);
  const [accountType, setAccountType] = useState<TribeAccountType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    tribe_name: '',
    description: '',
    member_count_max: 1,
    welcome_video_id: '',
    tribe_avatar_url: '',
    welcome_video_url: '',
    tribe_avatar_id: '',
    special_code: '',
    branding: null,
    integrations: null,
  });
  const [review360Email, setReview360Email] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FormData, string>>
  >({});

  useEffect(() => {
    if (id) {
      const fetchCircleData = async () => {
        setLoading(true);
        try {
          const circleData = await circleApi.getCircle(id);
          if (circleData.account_type) {
            setAccountType(circleData.account_type);
          }

          const formData: FormData = {
            tribe_name: circleData.tribe_name,
            description: circleData.description,
            member_count_max: circleData.member_count_max ?? 1,
            tribe_avatar_id: circleData.tribe_avatar_id,
            tribe_avatar_url: circleData.tribe_avatar_url ?? '',
            welcome_video_id: circleData.welcome_video_id,
            welcome_video_url: circleData.welcome_video?.url ?? '',
            special_code: circleData.special_code ?? '',
            branding: circleData.branding ?? null,
            integrations: circleData.integrations ?? null,
          };
          setFormData(formData);
          setInitialData(formData);
          setReview360Email(integrationsEmail(formData.integrations));
        } catch (error) {
          setError('Failed to fetch circle data.');
        } finally {
          setLoading(false);
        }
      };

      fetchCircleData();
    }
  }, [id]);

  const [error, setError] = useState<string>('');

  const handleInputChange = useCallback(
    (field: keyof FormData) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prevData) => ({
          ...prevData,
          [field]: event.target.value,
        }));
        setFieldErrors((prevErrors) => {
          const { [field]: removedError, ...restErrors } = prevErrors;
          return restErrors;
        });
      },
    []
  );

  const handleSelectChange = useCallback(
    (field: keyof FormData) => (event: SelectChangeEvent) => {
      setFormData((prevData) => ({
        ...prevData,
        [field]: Number(event.target.value),
      }));
    },
    []
  );

  const handleVideoUpdate = (fileId: string | File) => {
    if (typeof fileId === 'string') {
      setFormData((prev) => ({
        ...prev,
        welcome_video_id: fileId,
      }));
      setFieldErrors((prevErrors) => {
        const { welcome_video_id, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  };

  const handleBrandingChange = useCallback(
    (branding: CircleBranding | null) => {
      setFormData((prevData) => ({ ...prevData, branding }));
    },
    []
  );

  const handleReview360EmailChange = useCallback((email: string) => {
    setReview360Email(email);
    setFormData((prevData) => ({
      ...prevData,
      integrations: integrationsFromEmail(email),
    }));
    setFieldErrors((prevErrors) => {
      const { integrations, ...restErrors } = prevErrors;
      return restErrors;
    });
  }, []);

  const handleImageUpdate = (fileId: string | File) => {
    if (typeof fileId === 'string') {
      setFormData((prevData) => ({
        ...prevData,
        tribe_avatar_id: fileId,
      }));
      setFieldErrors((prevErrors) => {
        const { tribe_avatar_id, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  };

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.tribe_name.trim()) {
      newErrors.tribe_name = 'Circle name is required';
    }
    if (!formData.welcome_video_id) {
      newErrors.welcome_video_id = 'Video is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.tribe_avatar_id) {
      newErrors.tribe_avatar_id = 'Photo is required';
    }
    if (review360Email.trim() && !isPlausibleEmail(review360Email)) {
      newErrors.integrations = 'Enter the 360review admin email address';
    }

    setFieldErrors(newErrors);

    const firstErrorKey = Object.keys(newErrors)[0] as
      keyof FormData | undefined;
    if (firstErrorKey) {
      const scrollMap: Partial<
        Record<keyof FormData, React.RefObject<HTMLDivElement>>
      > = {
        tribe_name: nameRef,
        welcome_video_id: videoRef,
        description: descriptionRef,
        member_count_max: membersRef,
        tribe_avatar_id: imageRef,
      };

      const targetRef = scrollMap[firstErrorKey];
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }

    return Object.keys(newErrors).length === 0;
  }, [formData, review360Email]);

  const getChangedFields = (initial: FormData, current: FormData) => {
    const changedFields: Partial<FormData> = {};

    (Object.keys(current) as (keyof FormData)[]).forEach((key) => {
      if (JSON.stringify(initial[key]) !== JSON.stringify(current[key])) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        changedFields[key] = current[key];
      }
    });

    return changedFields;
  };

  const editCircle = useCallback(async () => {
    if (!validateForm()) {
      return;
    }
    if (!initialData) {
      return;
    }

    const payload = {
      ...getChangedFields(initialData, formData),
      tribe_name: formData.tribe_name.trim(),
      description: formData.description,
      member_count_max: formData.member_count_max,
      welcome_video_id: formData.welcome_video_id,
      tribe_avatar_id: formData.tribe_avatar_id,
      special_code: formData.special_code ?? '',
    };
    setIsSaving(true);
    setError('');
    // might want to use state to control the page update instead of timeout
    try {
      await circleApi.editCircle(payload, id);
      setTimeout(() => {
        dispatch(fetchLoggedUser());
        navigate(`/circle/${id}`);
      }, 7000);
    } catch (saveError) {
      // The API's own reason (for example "Circle billing status of UNPAID
      // is not PAID") used to be swallowed: the spinner stopped and nothing
      // said why (Markus, 2026-10-24).
      setIsSaving(false);
      setError(saveErrorMessage(saveError));
    }
  }, [formData, validateForm, id, navigate, initialData, dispatch]);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await circleApi.closeCircle(id);
      dispatch(fetchLoggedUser());
      navigate(PathRoutes.ProfileRoute);
    } catch (error) {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loader}>
        <MaterialCircularProgress />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <IconButton
          aria-label="delete"
          className={styles.deleteButton}
          onClick={handleDeleteClick}
          color="error"
        >
          <DeleteIcon />
        </IconButton>

        <Header id={id} />
        <div ref={nameRef}>
          <CommonField
            title={NAME_FIELD.title}
            description={NAME_FIELD.description}
            placeholder={NAME_FIELD.placeholder}
            charactersLength={NAME_FIELD.charactersLength}
            anchor={'info-circle-name'}
            value={formData.tribe_name}
            setValue={handleInputChange('tribe_name')}
          />
          {fieldErrors.tribe_name && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 0.5, ml: 1, fontWeight: 700 }}
            >
              {fieldErrors.tribe_name}
            </Typography>
          )}
        </div>

        <div ref={videoRef}>
          <VideoUpload
            defaultVideo={formData.welcome_video_url}
            videoType="video-welcome"
            description={`Similar to the Description, this is your chance to pitch the purpose and value of your Circle.`}
            error={fieldErrors.welcome_video_id}
            onUpdate={handleVideoUpdate}
          />
        </div>

        <div ref={imageRef}>
          <UploadImage
            onUpdate={handleImageUpdate}
            defaultImage={
              typeof formData.tribe_avatar_url === 'string'
                ? formData.tribe_avatar_url
                : ''
            }
          >
            Add Photo
          </UploadImage>
          {fieldErrors.tribe_avatar_url && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 0.5, ml: 1, fontWeight: 700 }}
            >
              {fieldErrors.tribe_avatar_url}
            </Typography>
          )}
        </div>
        <div ref={descriptionRef}>
          <CommonField
            type="textarea"
            title={DESCRIPTION_FIELD.title}
            description={DESCRIPTION_FIELD.description}
            placeholder={DESCRIPTION_FIELD.placeholder}
            charactersLength={DESCRIPTION_FIELD.charactersLength}
            anchor={'info-description'}
            value={formData.description}
            setValue={handleInputChange('description')}
          />
          {fieldErrors.description && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 0.5, ml: 1, fontWeight: 700 }}
            >
              {fieldErrors.description}
            </Typography>
          )}
        </div>
        <CommonField
          title="Special code (optional)"
          charactersLength={12}
          value={formData.special_code ?? ''}
          setValue={handleInputChange('special_code')}
        />
        <BrandingSection
          circleName={formData.tribe_name}
          specialCode={formData.special_code}
          value={formData.branding}
          onChange={handleBrandingChange}
        />
        <Review360Section
          specialCode={formData.special_code}
          value={formData.integrations}
          draftEmail={review360Email}
          error={fieldErrors.integrations}
          onChange={handleReview360EmailChange}
        />
        {accountType &&
          accountType !== TribeAccountType.FREE &&
          accountType !== TribeAccountType.DEMO && (
            <Members
              value={formData.member_count_max}
              accountType={accountType}
              setValue={handleSelectChange('member_count_max')}
            />
          )}

        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mb: 1, mt: 1, textAlign: 'center' }}
          >
            {error}
          </Typography>
        )}
        <button className={styles.btn} onClick={editCircle} disabled={isSaving}>
          {isSaving ? (
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          ) : (
            'Save Circle'
          )}
        </button>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{'Delete Circle?'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this Circle? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={
                isDeleting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};
